import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { X, Search, Plus } from "lucide-react";

if (typeof window !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement("#root");
}

const formatPrice = (val) => {
  if (val == null || val === "") return "₱0";
  const n = Number(val);
  return isNaN(n) ? String(val) : `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
};

const AddInventoryItemModal = ({
  isOpen,
  onClose,
  onAdd,
  products = [],
  maxResults = 5
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery("");
    setSelectedItems([]);
  }, [isOpen]);

  const filteredResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = Array.isArray(products) ? products : [];
    const filtered = q
      ? list.filter((i) => {
          const sku = (i.sku || i.productSku || "").toLowerCase();
          const name = (i.item || i.description || i.name || i.productName || "").toLowerCase();
          return sku.includes(q) || name.includes(q);
        })
      : list;
    const limit = Number(maxResults) > 0 ? Number(maxResults) : 5;
    console.log("Filtered results:", filtered.slice(0, limit));
    return filtered.slice(0, limit);
  }, [products, searchQuery, maxResults]);

  const handleAddSuggested = (item) => {
    const key = item.productId ?? item.id ?? item.inventoryId ?? item.sku;
    const existing = selectedItems.find((s) => (s.productId ?? s.id ?? s.inventoryId ?? s.sku) === key);
    if (existing) return;
    const largestUomName =
      item.largestUOM ?? "";
    setSelectedItems((prev) => [
      ...prev,
      {
        id: item.id ?? item.productId,
        productId: item.productId ?? item.id,
        sku: item.sku || item.productSku || "-",
        description: item.description || item.item || item.name || item.productName || "-",
        qty: 1,
        UomName: largestUomName || "Box",
        uom: largestUomName || "Box",
        price: item.originalData?.ipxToBigStorePrice ?? 0,
        originalData: item.originalData ?? item
      }
    ]);
  };

  const handleCancel = () => {
    setSelectedItems([]);
    setSearchQuery("");
    onClose?.();
  };

  const handleConfirmAdd = () => {
    if (selectedItems.length && onAdd) {
      onAdd(selectedItems);
    }
    handleCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCancel}
      style={{
        overlay: { backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1000 },
        content: {
          position: "relative",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          maxWidth: "780px",
          width: "100%",
          maxHeight: "90vh",
          margin: "auto",
          padding: "0",
          outline: "none",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }
      }}
      contentLabel="Add Inventory Item"
    >
      <div className="add-item-modal-content" style={{ maxHeight: "94vh" }}>
        <button className="add-item-modal-close" onClick={handleCancel} aria-label="Close">
          <X size={24} />
        </button>
        <h2 className="add-item-modal-title">Add Inventory Item</h2>

        <div className="add-item-modal-search">
          <div className="add-item-modal-search-input-wrap">
            <Search className="add-item-modal-search-icon" size={18} />
            <input
              type="text"
              className="add-item-modal-search-input"
              placeholder="Search by SKU or name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button type="button" className="add-item-modal-btn-search" onClick={() => {}}>
            Search
          </button>
          <button type="button" className="add-item-modal-btn-scan" disabled>
            Scan Barcode
          </button>
        </div>

        <div className="add-item-modal-results">
          <h3 className="add-item-modal-section-title">Products</h3>
          <div className="add-item-modal-results-list" style={{ maxHeight: 360, overflowY: "auto" }}>
            {filteredResults.length === 0 ? (
              <div className="add-item-modal-empty">No products found</div>
            ) : (
              filteredResults.map((item, idx) => (
                <div key={item.sku || item.id || item.productId || idx} className="add-item-modal-result-row">
                  <span className="add-item-modal-result-sku">{item.sku || item.productSku || "-"}</span>
                  <span className="add-item-modal-result-desc">
                    {item.description || item.item || item.name || item.productName || "-"}
                  </span>
                  <span className="add-item-modal-result-price">
                    {formatPrice(item.originalData?.ipxToBigStorePrice ?? 0)}
                  </span>
                  <button
                    type="button"
                    className="add-item-modal-result-add"
                    onClick={() => handleAddSuggested(item)}
                    aria-label="Add"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="add-item-modal-selected" style={{ flex: 1, minHeight: 200 }}>
          <div className="add-item-modal-table-wrap" style={{ maxHeight: 320, overflowY: "auto" }}>
            <table className="add-item-modal-table">
              <thead>
                <tr>
                  <th>SKU Code</th>
                  <th>Item Description</th>
                  <th>QTY</th>
                  <th>Unit of Measure</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.sku}</td>
                    <td>{row.description}</td>
                    <td>{row.qty}</td>
                    <td>{row.uom}</td>
                    <td>{formatPrice(row.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="add-item-modal-footer">
          <button type="button" className="add-item-modal-btn-cancel" onClick={handleCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="add-item-modal-btn-add"
            onClick={handleConfirmAdd}
            disabled={selectedItems.length === 0}
          >
            Add
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddInventoryItemModal;

